import { Wordmark } from '#/components/layout/wordmark'
/**
 * `/invite/:token` — accept a workspace invite (moat plan §5.1).
 * Public preview via `GET /api/invite/:token`; accepting needs a session, then we land the
 * client on their first project (`/api/client/plans`).
 */
import { useState } from 'react'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { CheckCircle2, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { api, ApiError } from '#/lib/api'
import { useAcceptInvite, useInvitePreview, type ClientPlanListItem } from '#/lib/api-studio'
import { authClient } from '#/lib/auth-client'
import { m } from '#/paraglide/messages'

export const Route = createFileRoute('/invite/$token')({
  head: () => ({ meta: [{ title: m.st_inv_title() }] }),
  component: InvitePage,
})

const DEFAULT_ACCENT = '#2757e6'

function InvitePage() {
  const { token } = Route.useParams()
  const navigate = useNavigate()
  const { data: session } = authClient.useSession()
  const { data, isLoading, error } = useInvitePreview(token)
  const accept = useAcceptInvite(token)
  const [busy, setBusy] = useState(false)

  const invite = data?.invite
  const color = invite?.branding?.color && /^#[0-9a-fA-F]{6}$/.test(invite.branding.color) ? invite.branding.color : DEFAULT_ACCENT
  const signedIn = Boolean(session?.user)

  const go = async () => {
    const plans = await api<{ plans: ClientPlanListItem[] }>('/api/client/plans').catch(() => ({ plans: [] as ClientPlanListItem[] }))
    const first = plans.plans[0]
    if (first) await navigate({ to: '/client/$planId', params: { planId: first.id } })
    else await navigate({ to: '/client' })
  }

  const onAccept = () => {
    setBusy(true)
    accept.mutate(undefined, {
      onSuccess: () => void go().finally(() => setBusy(false)),
      onError: (err) => {
        setBusy(false)
        toast.error(err instanceof ApiError ? err.message : m.st_err_generic())
      },
    })
  }

  return (
    <div className="flex min-h-full items-center justify-center bg-slate-50 px-5 py-12 text-slate-900">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        {isLoading ? (
          <p className="text-sm text-slate-500">{m.st_loading()}</p>
        ) : error || !invite ? (
          <>
            <h1 className="font-heading text-xl font-semibold">{m.st_inv_title()}</h1>
            <p className="mt-2 text-sm text-slate-600">{m.st_inv_not_found()}</p>
          </>
        ) : (
          <>
            <div className="flex items-center gap-3">
              {invite.branding?.logoUrl ? <img src={invite.branding.logoUrl} alt="" className="size-10 rounded-lg object-contain" /> : <span className="inline-block size-10 rounded-lg" style={{ background: color }} />}
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold" style={{ color }}>
                  {invite.branding?.name ?? invite.workspaceName}
                </div>
                <div className="truncate text-xs text-slate-500">{m.st_inv_for_email({ email: invite.email })}</div>
              </div>
            </div>
            <h1 className="mt-4 font-heading text-xl font-semibold">{m.st_inv_heading({ workspace: invite.workspaceName })}</h1>
            <p className="mt-1.5 text-sm text-slate-600">{invite.role === 'client' ? m.st_inv_as_client() : invite.role === 'facilitator' ? m.tm_inv_as_facilitator() : m.st_inv_as_member()}</p>

            <div className="mt-5">
              {!signedIn ? (
                <Link
                  to="/sign-in"
                  search={{ redirect: `/invite/${token}` }}
                  className="inline-flex h-10 items-center justify-center rounded-lg px-4 text-sm font-medium text-white"
                  style={{ background: color }}
                >
                  {m.st_inv_signin()}
                </Link>
              ) : invite.accepted ? (
                <div className="flex flex-col gap-2">
                  <p className="inline-flex items-center gap-1.5 text-sm text-emerald-700">
                    <CheckCircle2 className="size-4" /> {m.st_inv_accepted()}
                  </p>
                  <button type="button" onClick={() => void go()} className="inline-flex h-10 w-fit items-center justify-center rounded-lg px-4 text-sm font-medium text-white" style={{ background: color }}>
                    {m.st_inv_open()}
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={onAccept}
                  disabled={busy || accept.isPending}
                  className="inline-flex h-10 items-center justify-center gap-1.5 rounded-lg px-4 text-sm font-medium text-white disabled:opacity-60"
                  style={{ background: color }}
                >
                  {busy || accept.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
                  {busy || accept.isPending ? m.st_inv_accepting() : m.st_inv_accept()}
                </button>
              )}
            </div>
          </>
        )}
        <p className="mt-6 text-xs text-slate-400">
          <span className="inline-flex items-center gap-2">{m.share_made_with()} <Wordmark to="/" size="sm" className="text-slate-900" /></span>
        </p>
      </div>
    </div>
  )
}
